import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import * as _ from 'lodash';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  Order,
  OrderCustomFields,
  OrderUpdate,
  OrderStatus,
} from '@app/types/order';
import { getCancelType } from '@app/utils/order';

const cancelReasons = [{ label: 'その他', value: 'その他' }] as const;

interface CancelModalProps {
  onClose: () => void;
  onSubmit?: (
    organizationId: string,
    orderId: string,
    payload: OrderUpdate
  ) => void;
  open: boolean;
  order?: Order;
}

export function CancelModal({
  onClose,
  onSubmit,
  open,
  order,
}: CancelModalProps): ReactElement {
  const [isLoading, setIsLoading] = useState(false);
  const { control, formState, getValues, handleSubmit, reset } =
    useForm<OrderUpdate>({
      defaultValues: {
        customFields: {
          ...order?.customFields,
          cancelMessage: order?.customFields?.cancelMessage || '',
        },
        otherReason: '',
      },
      mode: 'onSubmit',
    });

  const titleLabel = useMemo(() => {
    return order?.status === OrderStatus.PENDING
      ? '応募を辞退する理由を入力してください'
      : '採用決定後のキャンセルは医療機関に負担をかけるため、基本的にはご遠慮いただいております。それでもキャンセルが必要な場合、下記から理由をお選びください。';
  }, [order?.status]);

  const handleClose = useCallback((): void => {
    onClose();
    reset();
  }, [onClose, reset]);

  const onFormSubmit = useCallback(
    async (data: OrderUpdate) => {
      setIsLoading(true);
      const organizationId = _.get(order?.organization, 'id');
      const orderId = order?.id;
      if (!organizationId || !orderId) {
        setIsLoading(false);
        return;
      }

      if (data.customFields?.cancelMessage === 'その他') {
        data.customFields.cancelMessage = data.otherReason || '';
      }
      if (onSubmit) {
        onSubmit(organizationId, orderId, {
          canceledAt: new Date(),
          customFields: {
            ...data.customFields,
            cancelRequestAt: new Date().toISOString(),
            cancelType: getCancelType(order.status),
          } as OrderCustomFields,
          status: OrderStatus.CANCELED,
        });
      }
    },
    [onSubmit, order]
  );

  useEffect(() => {
    if (!open) {
      reset();
      setIsLoading(false);
    }
  }, [open, reset]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent>
        <Typography variant="body2">
          {titleLabel}
          <Typography component="span" color="error">
            *
          </Typography>
        </Typography>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Controller
            name="customFields.cancelMessage"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth>
                <Select {...field} error={!!error} margin="dense">
                  {cancelReasons.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          {getValues('customFields.cancelMessage') === 'その他' && (
            <Controller
              name="otherReason"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  margin="dense"
                  placeholder="理由を入力"
                  rows={3}
                  multiline
                  fullWidth
                />
              )}
            />
          )}
          <Stack direction="row" spacing={1}>
            <Button
              color="black"
              variant="outlined"
              fullWidth
              sx={{ typography: 'body2' }}
              onClick={handleClose}
            >
              キャンセル
            </Button>
            <LoadingButton
              color="black"
              variant="contained"
              loading={isLoading}
              disabled={!formState.isValid}
              fullWidth
              onClick={handleSubmit(onFormSubmit)}
              sx={{ typography: 'body2' }}
            >
              送信
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
